import { useCallback, useEffect } from "react";

import { configureSound, primeSound } from "@/lib/sound";
import { useDispatch, useSelector } from "@/store";
import { setVolume, toggleMuted } from "@/store/reducers/sound";

/**
 * Keeps the audio engine pointed at the player's preferences.
 *
 * Mounted once, at the top of the app, rather than wherever a sound happens.
 * The engine deliberately knows nothing about React or the store - it is handed
 * a level, it does not go looking for one - and the games call `play` straight
 * out of `@/lib/sound` with no hook at all. That only works if *somebody* keeps
 * the two in step, and the honest place for that is the app rather than a game
 * page, so a stale level cannot survive a route change.
 *
 * It also arms the first gesture. Browsers will not let a page make a sound
 * before it has been pressed, and plenty of these sounds fire out of a timer
 * well after the press that set them going - a keno draw, a mines bust - so
 * waiting for a click-borne `play()` to build the context would silence the
 * first round on a cold page.
 */
export const useSoundEngine = () => {
  const muted = useSelector((state) => state.sound.muted);
  const volume = useSelector((state) => state.sound.volume);

  useEffect(() => primeSound(), []);

  useEffect(() => configureSound({ muted, volume }), [muted, volume]);
};

/** The same preferences, for the control that changes them. */
export const useSoundPreference = () => {
  const dispatch = useDispatch();
  const muted = useSelector((state) => state.sound.muted);
  const volume = useSelector((state) => state.sound.volume);

  const toggle = useCallback(() => dispatch(toggleMuted()), [dispatch]);
  const setLevel = useCallback((next: number) => dispatch(setVolume(next)), [dispatch]);

  return { muted, volume, toggle, setLevel };
};
